import type { CSSProperties, ReactNode } from "react"
import DeckCard from "@/components/DeckBrowser/DeckCard"
import DeckChips from "@/components/DeckBrowser/DeckChips"
import { cardFace, cardIdsForFamily } from "@/content/cards"
import { GRID_FAMILY_DESC } from "@/content/cardsRound"
import type { Family } from "@/content/families"
import type { CardId } from "@/types"
import styles from "@/styles/components/DeckGrid.module.css"

/*
 * The deck browser's grid view — every family at once, cards all one size, on a
 * neutral cream ground (no family tint, per the handoff). Browser-only: it uses
 * `DeckCard`, never `DeckSpread`/`CardFace`, so the in-session pick is untouched.
 *
 * The chip row filters the grid — one family, or "All families" (default). A
 * card opens the same detail the card view does.
 */

const RM =
  typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches

interface DeckGridProps {
  inPlay: readonly Family[]
  /** Family number to show, or -1 for all families. */
  filter: number
  onFilter: (n: number) => void
  /** Open a card — handed its element so the overlay morphs from it. */
  onOpen: (id: CardId, el: HTMLElement) => void
  toggle?: ReactNode
}

export default function DeckGrid({ inPlay, filter, onFilter, onOpen, toggle }: DeckGridProps) {
  const shown = filter < 0 ? inPlay : inPlay.filter((family) => family.n === filter)

  // A slight tilt toward the cursor, as the handoff gives the grid cards.
  const onTilt = (event: React.MouseEvent) => {
    if (RM) return
    const art = event.currentTarget.firstElementChild as HTMLElement | null
    if (!art) return
    const r = (event.currentTarget as HTMLElement).getBoundingClientRect()
    const x = (event.clientX - r.left) / r.width - 0.5
    const y = (event.clientY - r.top) / r.height - 0.5
    art.style.transform = `translateY(-6px) rotateY(${x * 10}deg) rotateX(${-y * 8}deg)`
  }
  const resetTilt = (event: React.MouseEvent) => {
    const art = event.currentTarget.firstElementChild as HTMLElement | null
    if (art) art.style.transform = ""
  }

  return (
    <div className={styles.grid}>
      <DeckChips
        families={inPlay}
        active={filter}
        showAll
        onPick={(n) => onFilter(n)}
        toggle={toggle}
      />

      <div className={styles.families}>
        {shown.map((family) => (
          <section
            key={family.n}
            className={styles.section}
            style={{ "--fam-bar": `var(--family-${family.n}-deck-glow)` } as CSSProperties}
          >
            <div className={styles.head}>
              <h2 className={styles.title}>{family.name}</h2>
              <p className={styles.desc}>{GRID_FAMILY_DESC[family.n]}</p>
            </div>
            <div className={styles.cards}>
              {cardIdsForFamily(family.n).map((id) => (
                <button
                  key={id}
                  type="button"
                  className={styles.card}
                  aria-label={cardFace(id)?.name ?? id}
                  onClick={(event) => onOpen(id, event.currentTarget)}
                  onMouseMove={onTilt}
                  onMouseLeave={resetTilt}
                >
                  <DeckCard cardId={id} variant="grid" />
                </button>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  )
}
